/**
 * ThreadTagChips — flag-gated presentational rendering of a thread's workflow
 * tags (status / priority / owner / labels). Renders `null` unless
 * `GROUP_THREAD_TAGS_ENABLED` (or an explicit `enabled` override used by tests),
 * so it is inert in production until product enables the feature.
 *
 * Presentational + lightly interactive: pass `onCycleStatus` / `onRemoveLabel`
 * to make the status chip / label × affordances live. With no callbacks it is a
 * pure read-only render. It never talks to Firestore or the access port itself —
 * the parent owns data + authorization.
 */
import React from 'react';
import { isGroupThreadTagsEnabled } from '../../services/threadTags/featureFlags';
import type { TagPriority, TagStatus } from '../../services/threadTags/taxonomy';
import type { ThreadTagState } from '../../services/threadTags/types';
import {
  TagChip,
  TagChipButton,
  TagChipRemove,
  TagChipRow,
  type ChipTone,
} from './ThreadTagChips.styles';

const STATUS_LABEL: Record<TagStatus, string> = {
  open: 'Open',
  in_progress: 'In progress',
  blocked: 'Blocked',
  waiting: 'Waiting',
  resolved: 'Resolved',
  archived: 'Archived',
};

const STATUS_TONE: Record<TagStatus, ChipTone> = {
  open: 'neutral',
  in_progress: 'info',
  blocked: 'danger',
  waiting: 'warn',
  resolved: 'success',
  archived: 'muted',
};

const PRIORITY_LABEL: Record<TagPriority, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent',
};

const PRIORITY_TONE: Record<TagPriority, ChipTone> = {
  low: 'muted',
  normal: 'neutral',
  high: 'warn',
  urgent: 'danger',
};

export type ThreadTagChipsData = Pick<
  ThreadTagState,
  'status' | 'ownerUserId' | 'priority' | 'workflowLabels'
>;

export interface ThreadTagChipsProps {
  tags: ThreadTagChipsData | null | undefined;
  /** Test/override hook; defaults to the module flag (OFF). */
  enabled?: boolean;
  /** Map an owner user id to a display name (defaults to the raw id). */
  ownerLabel?: (userId: string) => string;
  /** Hide the priority chip when it is the default `normal`. Default: false. */
  hideNormalPriority?: boolean;
  /** When provided, the status chip becomes a button that cycles status. */
  onCycleStatus?: (current: TagStatus) => void;
  /** When provided, each label chip gets a × to remove it. */
  onRemoveLabel?: (label: string) => void;
  /** When provided, the priority chip becomes a button that cycles priority. */
  onCyclePriority?: (current: TagPriority) => void;
  /**
   * When provided, the owner chip becomes a button that cycles owner over
   * `[unassigned, ...participants]`. Rendered even while unassigned so there is
   * an affordance to assign one. The parent supplies only participant ids — the
   * server independently rejects a non-participant owner.
   */
  onCycleOwner?: (current: string | null) => void;
  /** Optional "add next label" affordance. */
  onAddLabel?: () => void;
  /** Optional test id passthrough. */
  'data-testid'?: string;
}

export const ThreadTagChips: React.FC<ThreadTagChipsProps> = ({
  tags,
  enabled,
  ownerLabel,
  hideNormalPriority = false,
  onCycleStatus,
  onRemoveLabel,
  onCyclePriority,
  onCycleOwner,
  onAddLabel,
  ...rest
}) => {
  if (!isGroupThreadTagsEnabled(enabled)) return null;
  if (!tags) return null;

  const showPriority = !(hideNormalPriority && tags.priority === 'normal');
  const ownerName =
    tags.ownerUserId != null ? (ownerLabel ? ownerLabel(tags.ownerUserId) : tags.ownerUserId) : null;

  return (
    <TagChipRow data-thread-tag-chips="" data-testid={rest['data-testid']}>
      {onCycleStatus ? (
        <TagChipButton
          type="button"
          $tone={STATUS_TONE[tags.status]}
          aria-label={`status: ${STATUS_LABEL[tags.status]} — click to change`}
          data-thread-tag-status=""
          onClick={() => onCycleStatus(tags.status)}
        >
          {STATUS_LABEL[tags.status]}
        </TagChipButton>
      ) : (
        <TagChip
          $tone={STATUS_TONE[tags.status]}
          aria-label={`status: ${STATUS_LABEL[tags.status]}`}
          data-thread-tag-status=""
        >
          {STATUS_LABEL[tags.status]}
        </TagChip>
      )}

      {showPriority && (onCyclePriority ? (
        <TagChipButton
          type="button"
          $tone={PRIORITY_TONE[tags.priority]}
          aria-label={`priority: ${PRIORITY_LABEL[tags.priority]} — click to change`}
          data-thread-tag-priority=""
          onClick={() => onCyclePriority(tags.priority)}
        >
          {PRIORITY_LABEL[tags.priority]}
        </TagChipButton>
      ) : (
        <TagChip
          $tone={PRIORITY_TONE[tags.priority]}
          aria-label={`priority: ${PRIORITY_LABEL[tags.priority]}`}
          data-thread-tag-priority=""
        >
          {PRIORITY_LABEL[tags.priority]}
        </TagChip>
      ))}

      {onCycleOwner ? (
        <TagChipButton
          type="button"
          $tone={ownerName != null ? 'neutral' : 'muted'}
          aria-label={
            ownerName != null
              ? `owner: ${ownerName} — click to change`
              : 'owner: unassigned — click to assign'
          }
          data-thread-tag-owner=""
          title={ownerName ?? 'Unassigned'}
          onClick={() => onCycleOwner(tags.ownerUserId ?? null)}
        >
          {ownerName != null ? `@${ownerName}` : 'Unassigned'}
        </TagChipButton>
      ) : (
        ownerName != null && (
          <TagChip
            $tone="neutral"
            aria-label={`owner: ${ownerName}`}
            data-thread-tag-owner=""
            title={ownerName}
          >
            @{ownerName}
          </TagChip>
        )
      )}

      {tags.workflowLabels.map((label) => (
        <TagChip
          key={label}
          $tone="muted"
          aria-label={`label: ${label}`}
          data-thread-tag-label={label}
          title={label}
        >
          {label}
          {onRemoveLabel && (
            <TagChipRemove
              type="button"
              aria-label={`remove label ${label}`}
              onClick={(e) => {
                e.stopPropagation();
                onRemoveLabel(label);
              }}
            >
              ×
            </TagChipRemove>
          )}
        </TagChip>
      ))}

      {onAddLabel && (
        <TagChipButton
          type="button"
          $tone="muted"
          aria-label="add next workflow label"
          data-thread-tag-add-label=""
          onClick={onAddLabel}
        >
          + label
        </TagChipButton>
      )}
    </TagChipRow>
  );
};

export default ThreadTagChips;
